import { PopoverClose } from '@radix-ui/react-popover';
import React from 'react';
import { PiArrowLeftLight, PiQuestionLight } from 'react-icons/pi';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { IconButton } from './button';

type FormHeaderProps = {
  title: string;
  backButton?: {
    isPopoverClose?: boolean; // Whether the back button is a PopoverClose
    onClick?: () => void; // Back button click handler
    testId?: string; // Test ID for the back button
  };
  rightContent?: React.ReactNode; // Optional right-side content (e.g., buttons/icons)
  leftContent?: React.ReactNode; // Optional left-side content (e.g., checkbox)
  tooltipText?: string; // Optional tooltip text for the question mark icon
};

const FormHeader: React.FC<FormHeaderProps> = ({
  title,
  backButton,
  rightContent,
  leftContent,
  tooltipText,
}) => {
  const renderBackButton = () => {
    if (!backButton) return null;

    const buttonContent = (
      <div
        className='p-1 cursor-pointer rounded-full hover:text-zinc-600 transition-colors'
        data-testid={backButton.testId}
        onClick={backButton.onClick}
      >
        <PiArrowLeftLight size={24} />
      </div>
    );

    if (backButton.isPopoverClose) {
      return <PopoverClose asChild>{buttonContent}</PopoverClose>;
    }

    return buttonContent;
  };

  return (
    <div className='flex p-3 items-center justify-between'>
      {/* Left Section: Back Button (Optional) and Title */}
      <div className='flex gap-2 items-center'>
        {renderBackButton()}
        {leftContent && <div>{leftContent}</div>}
        <label className='font-semibold text-base'>{title}</label>
      </div>

      {/* Right Section: Optional Content */}
      {rightContent && (
        <div className='flex items-end gap-1'>
          {tooltipText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton>
                    <PiQuestionLight size={24} className='cursor-pointer' />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {rightContent}
        </div>
      )}
    </div>
  );
};

export default FormHeader;
